import { fetchJson, performPostRequest } from '@atlassian/jira-fetch';
import {
	isSynthetic,
	X2C_SERVICE_URL_PREFIX,
} from '@atlassian/jira-project-create-importer-common';
import type {
	AccessTokenStatus,
	StartThirdPartyImportPayload,
	ThirdPartyEntity,
	ThirdPartyEntityPage,
	ThirdPartyEntityStats,
	ThirdPartyEntityStatuses,
	ThirdPartyFolderAndSubfolders,
	ThirdPartyMembers,
} from '../../../types';
import type { ThirdPartyClient, ThirdPartyFieldConfigData } from '../types';

export class TrelloClient implements ThirdPartyClient {
	private readonly BASE_URL: string;

	private readonly BASE_HEADERS: Record<string, string> = {
		'x-issynthetic-traffic': String(isSynthetic()),
	};

	constructor({ cloudId }: { cloudId: string }) {
		this.BASE_URL = `${X2C_SERVICE_URL_PREFIX}/${cloudId}/import/trello`;
	}

	startImport({
		entityId,
		payload,
	}: {
		entityId: string;
		payload: StartThirdPartyImportPayload;
	}): Promise<{ migrationId: string }> {
		const url = `${this.BASE_URL}/board/${entityId}/migration/start`;
		return performPostRequest(url, {
			body: JSON.stringify(payload),
			headers: {
				...this.BASE_HEADERS,
				'Content-Type': 'application/json',
			},
		});
	}

	getEntities({ workspaceId }: { workspaceId: string }) {
		const url = `${this.BASE_URL}/workspaces/${workspaceId}/boards`;

		return fetchJson<ThirdPartyEntity[]>(url, {
			method: 'GET',
			headers: this.BASE_HEADERS,
		});
	}

	getEntityStats({ entityId }: { entityId: string }): Promise<ThirdPartyEntityStats> {
		const url = `${this.BASE_URL}/board/${entityId}/stats`;

		return fetchJson<ThirdPartyEntityStats>(url, {
			method: 'GET',
			headers: {
				...this.BASE_HEADERS,
			},
		});
	}

	getEntityMembers({ entityId }: { entityId: string }): Promise<ThirdPartyMembers[]> {
		const url = `${this.BASE_URL}/board/${entityId}/members`;

		return fetchJson<ThirdPartyMembers[]>(url, {
			method: 'GET',
			headers: this.BASE_HEADERS,
		});
	}

	getWorkspaces(): Promise<ThirdPartyEntity[]> {
		const url = `${this.BASE_URL}/workspaces`;

		return fetchJson<ThirdPartyEntity[]>(url, {
			method: 'GET',
			headers: this.BASE_HEADERS,
		});
	}

	getFieldConfig(): Promise<ThirdPartyFieldConfigData> {
		return Promise.reject(new Error('getFieldConfig method is not implemented in Trello'));
	}

	validateAccessToken(): Promise<{ status: AccessTokenStatus }> {
		return Promise.reject(new Error('validateAccessToken method is not implemented in Trello'));
	}

	postAccessToken(): Promise<void> {
		return Promise.reject(new Error('postAccessToken method is not implemented in Trello'));
	}

	getEntitiesPage(): Promise<ThirdPartyEntityPage> {
		return Promise.reject(new Error('getEntitiesPage method is not implemented in Trello'));
	}

	searchEntities(): Promise<ThirdPartyEntity[]> {
		return Promise.reject(new Error('searchEntities method is not implemented in Trello'));
	}

	getFolders(): Promise<ThirdPartyFolderAndSubfolders> {
		return Promise.reject(new Error('getFolders method is not implemented in trello'));
	}

	getSiteName(): Promise<string> {
		return Promise.reject(new Error('getSiteName method is not implemented in Trello'));
	}

	getSpaces(): Promise<ThirdPartyEntity[]> {
		return Promise.reject(new Error('getSpaces method is not implemented in Trello'));
	}

	getMultiEntityStats(): Promise<ThirdPartyEntityStats> {
		return Promise.reject(new Error('getMultiEntityStats method is not implemented in Trello'));
	}

	getMultiEntityStatuses(): Promise<ThirdPartyEntityStatuses> {
		return Promise.reject(new Error('getMultiEntityStatuses method is not implemented in Trello'));
	}
}
